'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle } from 'lucide-react'

interface ValidationModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (config: ValidationConfig) => Promise<void>
  repositoryName: string
  quota?: { used: number; limit: number }
}

export interface ValidationConfig {
  validationType: 'analysis' | 'simulation' | 'both'
  branch: string
  configPath: string
}

export function ValidationModal({
  open,
  onClose,
  onSubmit,
  repositoryName,
  quota,
}: ValidationModalProps) {
  const [config, setConfig] = useState<ValidationConfig>({
    validationType: 'analysis',
    branch: 'main',
    configPath: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    try {
      await onSubmit(config)
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start validation')
    } finally {
      setLoading(false)
    }
  }

  const remainingQuota = quota ? quota.limit - quota.used : null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Run Validation</DialogTitle>
          <DialogDescription>
            Configure and run network validation for {repositoryName}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {quota && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>
                Daily quota: {quota.used}/{quota.limit} validations used
                {remainingQuota === 0 && ' (limit reached)'}
              </span>
            </div>
          )}

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="validationType" className="text-right">
              Type
            </Label>
            <Select
              value={config.validationType}
              onValueChange={(value: string) =>
                setConfig({ ...config, validationType: value as 'analysis' | 'simulation' | 'both' })
              }
            >
              <SelectTrigger className="col-span-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="analysis">
                  Analysis - Fast
                </SelectItem>
                <SelectItem value="simulation">
                  Simulation - Comprehensive
                </SelectItem>
                <SelectItem value="both">
                  Both - Complete Validation
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="branch" className="text-right">
              Branch
            </Label>
            <Input
              id="branch"
              value={config.branch}
              onChange={(e) =>
                setConfig({ ...config, branch: e.target.value })
              }
              className="col-span-3"
              placeholder="main"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="configPath" className="text-right">
              Config Path
            </Label>
            <Input
              id="configPath"
              value={config.configPath}
              onChange={(e) =>
                setConfig({ ...config, configPath: e.target.value })
              }
              className="col-span-3"
              placeholder="Optional: path/to/configs"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || remainingQuota === 0}
          >
            {loading ? 'Starting...' : 'Start Validation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}